import { assertError, assertType, log, Style, styledLog } from '@yp/common/core.ts';
import {
  isAbsent,
  isNumber,
  isObject,
  isPresent,
  isString,
  type Objectish,
  type TypeGuard,
} from '@yp/common/identity.ts';
import { type StrictPropertyList } from '@yp/common/schema/descriptor.ts';
import type { GaxiosError, GaxiosResponse } from 'googleapis-common';

// TODO: Unify with `secrets.ts`.
export const ErrorCode = {
  notFound: 5,
};

// interface Error {
//   code: number;
//   message: string;
//   errors: Array<{
//     domain: string;
//     reason: string;
//     message: string;
//   }>;
//   status?: string;
//   details?: Array<{
//     '@type': string;
//     reason: string;
//     domain: string;
//     metadata: unknown;
//   }>;
// }

// TODO: Build validator for these.
// TODO: SEe if we can use the existing declaration for GaxiosError.
type _GaxiosError2 = {
  code: number;
  message: string;
  errors: Array<{
    message: string;
    domain: string;
    reason: string;
  }>;
  status: string;
  details: Array<{
    '@type': string;
    // Type-specific fields.
  }>;
};

const _gaxiosErrorDescriptor: StrictPropertyList<_GaxiosError2> = [
  { k: 'code', t: 'number', req: true },
  { k: 'message', t: 'string', req: true },
  { k: 'status', t: 'string', req: true },
  // { k: 'errors', type: 'array' },
] as const;

// https://developers.google.com/drive/api/guides/handle-errors
export function handleGaxiosError(err: GaxiosError) {
  // DNS: We really need color for these errors and usage.
  if (!err.response) {
    return false;
  }

  const resp = err.response as GaxiosResponse<Objectish>;
  styledLog((b) => b.setStyled(Style.error, '\nGoogle API call failed.'));
  if (isPresent(resp.config.url)) {
    styledLog((b) =>
      b.setStyled(Style.error, 'Url: ').override(Style.string, resp.config.url!.toString()),
    );
  }
  styledLog((b) =>
    b
      .setStyled(Style.error, 'GaxiosError code ')
      .override(Style.number, String(resp.status))
      .add(' ')
      .override(Style.string, resp.statusText),
  );
  styledLog((b) => b.add('\n').setStyled(Style.comment, err.message).add('\n'));

  let recognizedError = false;
  if ('error' in resp.data) {
    const error = resp.data.error;
    if (resp.status === 401 && error === 'unauthorized_client') {
      console.log(
        'See https://developers.google.com/identity/protocols/oauth2/service-account#error-codes for details.',
      );
      console.log('Also, double-check that your configured project matches the service account:');
      console.log('  cli.sh gcloud auth check');
      console.log("Finally, you can't delegate to a group email.");
      recognizedError = true;
    } else if (isObject(error) && error.status === 'PERMISSION_DENIED') {
      const tokenScope = Array.isArray(error.details)
        ? error.details.find(
            <TypeGuard<Objectish>>(
              ((d) => isObject(d) && d.reason === 'ACCESS_TOKEN_SCOPE_INSUFFICIENT')
            ),
          )
        : undefined;
      if (tokenScope) {
        console.log(error.message, tokenScope.metadata);
        console.log('Run `cli.sh gcloud auth check` to verify your current scopes, and');
        console.log('`cli.sh gcloud auth login <scopes>` to update them.');
        recognizedError = true;
      }
    }
  }
  if (!recognizedError) {
    for (const [k, v] of Object.entries(resp.data)) {
      log(k, { v }, { defaultStyle: Style.error, verbosity: 'max' });
    }
  }
  return true;
}

interface FirebaseError {
  /**
   * Error codes are strings using the following format: `"service/string-code"`.
   * Some examples include `"auth/invalid-uid"` and
   * `"messaging/invalid-recipient"`.
   *
   * While the message for a given error can change, the code will remain the same
   * between backward-compatible versions of the Firebase SDK.
   */
  code: string;
  /**
   * An explanatory message for the error that just occurred.
   *
   * This message is designed to be helpful to you, the developer. Because
   * it generally does not convey meaningful information to end users,
   * this message should not be displayed in your application.
   */
  message: string;
  /**
   * A string value containing the execution backtrace when the error originally
   * occurred.
   *
   * This information can be useful for troubleshooting the cause of the error with
   * {@link https://firebase.google.com/support | Firebase Support}.
   */
  stack?: string;
}

export function isFirebaseError(err: unknown, code?: string): err is FirebaseError {
  assertError(err);
  if (
    err.constructor.name.startsWith('Firebase') ||
    err.constructor.name.startsWith('_Firebase') ||
    ('code' in err && isString(err.code) && 'message' in err && isString(err.message))
  ) {
    assertType<FirebaseError>(err);
    return isAbsent(code) || code === err.code;
  }
  return false;
}

interface GCloudError {
  code: number;
}

export function isGCloudError(err: unknown): err is GCloudError {
  if (!(err instanceof Error)) {
    return false;
  }
  if ('code' in err && isNumber(err.code)) {
    return true;
  }
  return false;
}

export function handleFireError(err: unknown): never;
export function handleFireError(
  err: unknown,
  continueCode: string | number,
  ...continueCodes: (string | number)[]
): void;
export function handleFireError(err: unknown, ...continueCodes: (string | number)[]) {
  if (!isFirebaseError(err) && !isGCloudError(err)) {
    console.error('Unexpected error:', err);
    throw err;
  }

  if (!continueCodes.includes(err.code)) {
    console.error(`Unexpected ${isString(err.code) ? 'Firebase' : 'GCloud'} error:`, err.code);
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw err;
  }
}
